/**
 * Client-side preview of an employee CSV, for the Import data review step.
 *
 * This only *describes* the file: the upload still sends the original file to
 * the backend, which parses and validates it again (backend/src/utils/csvParser.ts
 * and importEmployeesFromCSV). The rules below mirror that parser — same
 * papaparse options, same checks in the same order — so what the preview flags
 * is what the server would reject. Checks that need the employee table (is this
 * a new employee?) can't run here, so they surface as "Needs review" instead.
 *
 * Row numbers: "Row 1" is the first row under the header, as in the server's
 * own validation messages. The server forwards papaparse's errors as
 * `Row ${err.row}` unchanged, and papaparse numbers those inconsistently:
 * field-count errors use the 0-based data index, quote errors count the header
 * row (so they read 1-based). `parseServerErrors` corrects for that; if the
 * backend starts adding 1 to field-count rows, drop `zeroBased` below.
 */
import Papa from 'papaparse';
import type { ParseError } from 'papaparse';

export const REQUIRED_COLUMNS = ['name', 'email', 'role', 'department'] as const;
export const OPTIONAL_COLUMNS = ['startDate', 'customAllotment', 'ptoAllotment', 'sickTimeAllotment', 'wfhAllotment'] as const;
const KNOWN_COLUMNS = new Set<string>([...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS]);

/** The server's upload limit (multer, 13 MB). */
export const MAX_FILE_BYTES = 13 * 1024 * 1024;

export type RowTone = 'ready' | 'review' | 'error';

export interface RowIssue {
  tone: 'review' | 'error';
  message: string;
}

export interface PreviewRow {
  /** 1-based position under the header, matching the server's "Row N" messages. */
  rowNumber: number;
  name: string;
  email: string;
  role: string;
  department: string;
  /** Normalized YYYY-MM-DD, or null when blank or not a real date. */
  startDate: string | null;
  /** The cell as typed, shown when it can't be read as a date. */
  startDateRaw: string;
  customAllotment: boolean;
  pto: number | null;
  sick: number | null;
  wfh: number | null;
  issues: RowIssue[];
  tone: RowTone;
  /** A neutral line under the status, e.g. what the server confirmed. */
  note?: string;
}

export interface PreviewCounts {
  ready: number;
  review: number;
  error: number;
}

export interface CsvPreview {
  rows: PreviewRow[];
  /** False for a blank file: no header row at all. */
  hasHeader: boolean;
  /** Required headers the file doesn't have (headers are case-sensitive). */
  missingColumns: string[];
  /** Headers the import ignores. */
  ignoredColumns: string[];
  /** Distinct department names on rows that pass the checks. */
  departments: number;
  counts: PreviewCounts;
}

type CsvRecord = Record<string, string | undefined>;

/** Same rule as the backend: YYYY-MM-DD or M/D/YYYY, and a real calendar date. */
export const parseCsvDate = (raw: string): string | null => {
  const value = raw.trim();
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  let y: number;
  let m: number;
  let d: number;
  if (iso) {
    y = Number(iso[1]);
    m = Number(iso[2]);
    d = Number(iso[3]);
  } else if (us) {
    m = Number(us[1]);
    d = Number(us[2]);
    y = Number(us[3]);
  } else {
    return null;
  }
  const parsed = new Date(Date.UTC(y, m - 1, d));
  if (parsed.getUTCFullYear() !== y || parsed.getUTCMonth() !== m - 1 || parsed.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/** Blank → undefined ("not supplied"); otherwise a whole number ≥ 0, or NaN. */
const parseOptionalInt = (raw: string | undefined): number | undefined => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : NaN;
};

const EMAIL_PATTERN = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

export const joinWords = (words: string[]) =>
  words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

type ParseCode = ParseError['code'];

const PARSE_MESSAGES: Partial<Record<ParseCode, string>> = {
  TooFewFields: 'This row has fewer columns than the header. Check for a missing comma or a line break inside a cell.',
  TooManyFields: 'This row has more columns than the header. Put values that contain commas in double quotes.',
  MissingQuotes: 'A quoted value isn’t closed. Check this row for a stray double quote.',
  InvalidQuotes: 'A quoted value isn’t closed. Check this row for a stray double quote.',
  UndetectableDelimiter: 'Couldn’t tell how the columns are separated. Save the sheet as a comma-separated .csv file.',
};

/** Quote errors count the header row, so their `row` is one past the data index. */
const QUOTE_CODES = new Set<ParseCode>(['MissingQuotes', 'InvalidQuotes']);

/** papaparse's own messages, as the server forwards them ("Row N: <message>"). */
const PAPA_SERVER_MESSAGES: { pattern: RegExp; code: ParseCode; zeroBased: boolean }[] = [
  { pattern: /^Too many fields/i, code: 'TooManyFields', zeroBased: true },
  { pattern: /^Too few fields/i, code: 'TooFewFields', zeroBased: true },
  { pattern: /^Quoted field unterminated/i, code: 'MissingQuotes', zeroBased: false },
  { pattern: /^Trailing quote/i, code: 'InvalidQuotes', zeroBased: false },
  { pattern: /^Unable to auto-detect/i, code: 'UndetectableDelimiter', zeroBased: false },
];

const parseErrorMessage = (code: ParseCode, fallback: string) => PARSE_MESSAGES[code] ?? fallback;

const toneOf = (issues: RowIssue[]): RowTone =>
  issues.some((issue) => issue.tone === 'error') ? 'error' : issues.length > 0 ? 'review' : 'ready';

export const countRows = (rows: PreviewRow[]): PreviewCounts =>
  rows.reduce<PreviewCounts>(
    (acc, row) => {
      acc[row.tone] += 1;
      return acc;
    },
    { ready: 0, review: 0, error: 0 },
  );

export const buildPreview = (csvText: string): CsvPreview => {
  const result = Papa.parse<CsvRecord>(csvText, { header: true, skipEmptyLines: true });
  const fields = (result.meta.fields ?? []).filter((field) => field.trim() !== '');
  const fieldSet = new Set(fields);

  const parseIssuesByIndex = new Map<number, RowIssue[]>();
  result.errors.forEach((error) => {
    if (typeof error.row !== 'number') return;
    const index = QUOTE_CODES.has(error.code) ? error.row - 1 : error.row;
    const list = parseIssuesByIndex.get(index) ?? [];
    list.push({ tone: 'error', message: parseErrorMessage(error.code, error.message) });
    parseIssuesByIndex.set(index, list);
  });

  const firstRowByEmail = new Map<string, number>();
  const departments = new Set<string>();

  const rows = result.data.map((record, index): PreviewRow => {
    const rowNumber = index + 1;
    const name = (record.name || '').trim();
    const email = (record.email || '').trim().toLowerCase();
    const role = (record.role || '').trim();
    const department = (record.department || '').trim();
    const startDateRaw = (record.startDate || '').trim();
    const customAllotment = /^(true|yes|y|1)$/i.test((record.customAllotment || '').trim());
    const ptoParsed = parseOptionalInt(record.ptoAllotment);
    const sickParsed = parseOptionalInt(record.sickTimeAllotment);
    const wfhParsed = parseOptionalInt(record.wfhAllotment);
    const startDate = startDateRaw ? parseCsvDate(startDateRaw) : null;

    const issues: RowIssue[] = [...(parseIssuesByIndex.get(index) ?? [])];

    // The server stops at the first failed check on a row, in this order.
    const validationError = (() => {
      const missing = REQUIRED_COLUMNS.filter((column) => !{ name, email, role, department }[column]);
      if (missing.length > 0) {
        return `Missing ${joinWords(missing)}. Every row needs a name, email, role and department.`;
      }
      if (!EMAIL_PATTERN.test(email)) {
        return `“${email}” isn’t a valid email address. Use the full work address, like name@company.com.`;
      }
      const badNumber = ([
        ['ptoAllotment', record.ptoAllotment, ptoParsed],
        ['sickTimeAllotment', record.sickTimeAllotment, sickParsed],
        ['wfhAllotment', record.wfhAllotment, wfhParsed],
      ] as const).find(([, , parsed]) => parsed !== undefined && Number.isNaN(parsed));
      if (badNumber) {
        return `${badNumber[0]} is “${(badNumber[1] || '').trim()}”. Use a whole number, 0 or more.`;
      }
      if (startDateRaw && !startDate) {
        return `startDate “${startDateRaw}” isn’t a real date. Use YYYY-MM-DD or M/D/YYYY.`;
      }
      return null;
    })();

    if (validationError) {
      issues.push({ tone: 'error', message: validationError });
    } else {
      departments.add(department);
      const firstRow = firstRowByEmail.get(email);
      if (firstRow !== undefined) {
        issues.push({ tone: 'error', message: `Same email as row ${firstRow}. List each employee once.` });
      } else {
        firstRowByEmail.set(email, rowNumber);
        // These depend on whether the employee already exists, which only the
        // server knows: new employees fail them, existing employees pass.
        if (!customAllotment && !startDate) {
          issues.push({
            tone: 'review',
            message: 'No startDate. Required if this is a new employee; existing employees keep the date on file.',
          });
        }
        if (customAllotment && (ptoParsed === undefined || sickParsed === undefined)) {
          issues.push({
            tone: 'review',
            message:
              'customAllotment is true but ptoAllotment or sickTimeAllotment is blank. New employees, and anyone switching to custom, need both.',
          });
        }
      }
    }

    const finite = (value: number | undefined) => (value === undefined || Number.isNaN(value) ? null : value);
    return {
      rowNumber,
      name,
      email,
      role,
      department,
      startDate,
      startDateRaw,
      customAllotment,
      pto: finite(ptoParsed),
      sick: finite(sickParsed),
      wfh: finite(wfhParsed),
      issues,
      tone: toneOf(issues),
    };
  });

  return {
    rows,
    hasHeader: fields.length > 0,
    missingColumns: fields.length > 0 ? REQUIRED_COLUMNS.filter((column) => !fieldSet.has(column)) : [],
    ignoredColumns: fields.filter((field) => !KNOWN_COLUMNS.has(field)),
    departments: departments.size,
    counts: countRows(rows),
  };
};

/* ------------------------------------------------------------------------ */
/* Server validation errors → rows                                           */
/* ------------------------------------------------------------------------ */

export interface ServerIssues {
  byRow: Map<number, string[]>;
  byEmail: Map<string, string[]>;
  /** Messages that don't name a row or an email. */
  general: string[];
  /** Every message, ready to list: rows renumbered to match the table. */
  messages: string[];
}

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const push = <K>(map: Map<K, string[]>, key: K, message: string) => {
  map.set(key, [...(map.get(key) ?? []), message]);
};

/**
 * The server reports "Row N: …" for parse checks and "Name (email): …" for
 * checks against existing employees. Index both so the table can mark rows,
 * and renumber papaparse's rows so the list and the table agree.
 */
export const parseServerErrors = (errors: string[]): ServerIssues => {
  const issues: ServerIssues = { byRow: new Map(), byEmail: new Map(), general: [], messages: [] };
  errors.forEach((raw) => {
    const byRow = raw.match(/^Row (\d+|undefined): (.+)$/);
    if (byRow) {
      const papa = PAPA_SERVER_MESSAGES.find((entry) => entry.pattern.test(byRow[2]));
      const message = papa ? parseErrorMessage(papa.code, byRow[2]) : capitalize(byRow[2]);
      if (byRow[1] === 'undefined') {
        issues.general.push(message);
        issues.messages.push(message);
        return;
      }
      const rowNumber = Number(byRow[1]) + (papa?.zeroBased ? 1 : 0);
      push(issues.byRow, rowNumber, message);
      issues.messages.push(papa ? `Row ${rowNumber}: ${message}` : raw);
      return;
    }
    issues.messages.push(raw);
    const byEmail = raw.match(/^.*\(([^()\s]+@[^()\s]+)\): (.+)$/);
    if (byEmail) {
      push(issues.byEmail, byEmail[1].trim().toLowerCase(), capitalize(byEmail[2]));
      return;
    }
    issues.general.push(raw);
  });
  return issues;
};

/**
 * Marks rows the server rejected. A row the preview already flagged as an
 * error keeps its own (more specific) message; a "Needs review" row the server
 * rejected becomes an error carrying the server's reason.
 *
 * Name-and-email messages come from the server's second pass, which only runs
 * once every row parsed and checks every row against the employee table. So
 * when that is all the server sent, a "Needs review" row it didn't name passed:
 * it's an existing employee, and it's ready.
 */
export const applyServerIssues = (preview: CsvPreview, server: ServerIssues | null): CsvPreview => {
  if (!server || (server.byRow.size === 0 && server.byEmail.size === 0)) return preview;
  const checkedEveryRow = server.byRow.size === 0;
  const rows = preview.rows.map((row): PreviewRow => {
    if (row.tone === 'error') return row;
    // Duplicate-email messages name both copies; the preview already marks the repeat.
    const emailMessages = (row.email ? server.byEmail.get(row.email) ?? [] : []).filter((message) => !/duplicate email/i.test(message));
    const messages = [...(server.byRow.get(row.rowNumber) ?? []), ...emailMessages];
    if (messages.length > 0) {
      return { ...row, issues: messages.map((message) => ({ tone: 'error', message })), tone: 'error' };
    }
    if (row.tone === 'review' && checkedEveryRow) {
      return { ...row, issues: [], tone: 'ready', note: 'Checked by the server: existing employee.' };
    }
    return row;
  });
  return { ...preview, rows, counts: countRows(rows) };
};
